// defectDatabaseApi.js - API for defects database search

import defectsDatabase from '../assets/building_defects_database.json';

// Search defects database locally
export const searchDefectsDatabase = async (searchQuery = '', filters = {}) => {
  try {
    console.log('🔍 Searching defects database:', { searchQuery, filters });
    
    if (!searchQuery || searchQuery.length < 2) {
      return [];
    }
    
    const language = filters.language || 'de';
    const query = searchQuery.toLowerCase();
    
    const results = defectsDatabase.defects.filter(defect => {
      // Search in element names
      const elementMatch = defect.element?.[language]?.toLowerCase().includes(query) ||
                          defect.element?.de?.toLowerCase().includes(query) ||
                          defect.element?.en?.toLowerCase().includes(query);
      
      // Search in descriptions
      const descMatch = defect.description?.[language]?.toLowerCase().includes(query) ||
                       defect.description?.de?.toLowerCase().includes(query) ||
                       defect.description?.en?.toLowerCase().includes(query);
      
      // Search in defect types
      const typeMatch = defect.defectType?.[language]?.toLowerCase().includes(query) ||
                       defect.defectType?.de?.toLowerCase().includes(query) ||
                       defect.defectType?.en?.toLowerCase().includes(query);
      
      // Search in searchTerms (corrected field name)
      const searchTermsMatch = defect.searchTerms?.[language]?.some(term => 
        term.toLowerCase().includes(query)
      ) || defect.searchTerms?.de?.some(term => 
        term.toLowerCase().includes(query)
      ) || defect.searchTerms?.en?.some(term => 
        term.toLowerCase().includes(query)
      );
      
      let matches = elementMatch || descMatch || typeMatch || searchTermsMatch;
      
      // Apply filters
      if (filters.buildingPart && defect.buildingPart !== filters.buildingPart) {
        matches = false;
      }
      if (filters.category && defect.category !== filters.category) {
        matches = false;
      }
      if (filters.priority && defect.priority !== filters.priority) {
        matches = false;
      }
      
      return matches;
    });
    
    console.log('📊 Search results:', results.length, 'found for:', searchQuery);
    return results;
  } catch (error) {
    console.error('Error searching defects database:', error);
    return [];
  }
};

// Get defect suggestions by element name
export const getDefectSuggestionsByElement = async (elementName, language = 'de') => {
  try {
    console.log('💡 Getting suggestions for element:', elementName, 'language:', language);
    
    if (!elementName) {
      return [];
    }
    
    const query = elementName.toLowerCase();
    
    // Enhanced mapping for common building elements
    const elementMappings = {
      'außenwände': ['außenwand', 'fassade', 'putz', 'klinker', 'wand'],
      'außenwand': ['fassade', 'putz', 'klinker', 'außenwände'],
      'fassade': ['außenwand', 'putz', 'klinker', 'außenwände'],
      'wand': ['außenwand', 'fassade', 'putz', 'klinker'],
      'dach': ['dachdeckung', 'ziegel', 'dachrinne', 'dachstuhl'],
      'fenster': ['verglasung', 'rahmen', 'dichtung'],
      'tür': ['türrahmen', 'dichtung', 'schloss'],
      'keller': ['untergeschoss', 'foundation', 'basement']
    };
    
    const searchTerms = [query];
    if (elementMappings[query]) {
      searchTerms.push(...elementMappings[query]);
    }
    
    const suggestions = defectsDatabase.defects.filter(defect => {
      return searchTerms.some(term => {
        const elementMatch = defect.element?.[language]?.toLowerCase().includes(term) ||
                            defect.element?.de?.toLowerCase().includes(term) ||
                            defect.element?.en?.toLowerCase().includes(term);
        
        const searchTermsMatch = defect.searchTerms?.[language]?.some(searchTerm => 
          searchTerm.toLowerCase().includes(term)
        ) || defect.searchTerms?.de?.some(searchTerm => 
          searchTerm.toLowerCase().includes(term)
        ) || defect.searchTerms?.en?.some(searchTerm => 
          searchTerm.toLowerCase().includes(term)
        );
        
        return elementMatch || searchTermsMatch;
      });
    });
    
    console.log('📋 Element suggestions found:', suggestions.length, 'for:', elementName);
    return suggestions.slice(0, 5); // Limit to top 5 suggestions
  } catch (error) {
    console.error('Error getting defect suggestions:', error);
    return [];
  }
};

// Get defects by category with pagination
export const getDefectsByCategory = async (category, page = 1, limit = 10) => {
  try {
    console.log('📂 Getting defects by category:', category, 'page:', page, 'limit:', limit);
    
    const categoryDefects = defectsDatabase.defects.filter(defect => 
      defect.category === category
    );
    
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedDefects = categoryDefects.slice(startIndex, endIndex);
    
    const result = {
      defects: paginatedDefects,
      total: categoryDefects.length,
      hasMore: endIndex < categoryDefects.length,
      currentPage: page,
      totalPages: Math.ceil(categoryDefects.length / limit)
    };
    
    console.log('📊 Category results:', result.defects.length, 'of', result.total, 'total');
    return result;
  } catch (error) {
    console.error('Error getting defects by category:', error);
    return { defects: [], total: 0, hasMore: false };
  }
};

// Get all categories available in the database
export const getAvailableCategories = async () => {
  try {
    const categories = [...new Set(defectsDatabase.defects.map(defect => defect.category))];
    console.log('🏷️ Available categories:', categories);
    return categories;
  } catch (error) {
    console.error('Error getting categories:', error);
    return [];
  }
};

// Get all building parts available in the database
export const getAvailableBuildingParts = async () => {
  try {
    const buildingParts = [...new Set(defectsDatabase.defects.map(defect => defect.buildingPart))];
    console.log('🏗️ Available building parts:', buildingParts);
    return buildingParts;
  } catch (error) {
    console.error('Error getting building parts:', error);
    return [];
  }
};
